import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { Tooltip } from '@mui/material';
import type {
  GridColDef,
  GridRowId,
  GridRowModesModel,
  GridRowParams,
} from '@mui/x-data-grid';
import React from 'react';
import { GridActionsCellItem, GridRowModes } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import { TableName } from '../../../constants/table';
import { useGetStockMaster } from '../../../lib/query/stockMaster';
import { deleteEntity } from '../../../lib/services/api-service';

const commonColDef: Partial<GridColDef> = {
  editable: true,
  flex: 1,
  headerClassName: 'gridHeader--header',
};

export const Columns = (
  rowModesModel: GridRowModesModel,
  setRowModesModel: React.Dispatch<React.SetStateAction<GridRowModesModel>>,
  tenantId: string,
  onManageMappings?: (
    stockId: string,
    stockName: string,
    stockOCR: string,
    partitionKey: string,
    matchingStockAliases: string[],
  ) => void,
): GridColDef[] => {
  const { refetch } = useGetStockMaster(tenantId);

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleDelete =
    (PartitionKey: string, RowKey: string, StockDebitLedger: string) =>
    async () => {
      await deleteEntity(TableName.StockMaster, PartitionKey, RowKey).then(
        () => {
          toast.success(`Deleted Stock with name: ${StockDebitLedger}`);
          refetch();
        },
      );
    };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  return [
    {
      field: 'CompanyName',
      headerName: 'CompanyName',
      type: 'string',
      ...commonColDef,
      editable: false,
    },
    {
      field: 'VendorName',
      headerName: 'VendorName',
      type: 'string',
      ...commonColDef,
      editable: false,
    },

    {
      field: 'StockOCR',
      headerName: 'StockOCR',
      type: 'string',
      ...commonColDef,
      editable: false,
    },
    {
      field: 'StockDebitLedger',
      headerName: 'StockDebitLedger',
      type: 'string',
      ...commonColDef,
    },
    {
      field: 'UnitOfMeasurement',
      headerName: 'UnitOfMeasurement',
      type: 'string',
      ...commonColDef,
    },
    {
      field: 'CGSTRate',
      headerName: 'CGSTRate',
      type: 'number',
      ...commonColDef,
    },
    {
      field: 'SGSTRate',
      headerName: 'SGSTRate',
      type: 'number',
      ...commonColDef,
    },
    {
      field: 'IGSTRate',
      headerName: 'IGSTRate',
      type: 'number',
      ...commonColDef,
    },
    {
      field: 'GSTRate',
      headerName: 'GSTRate',
      type: 'number',
      ...commonColDef,
    },
    {
      field: 'HSNSAC',
      headerName: 'HSNSAC',
      type: 'string',
      ...commonColDef,
    },
    {
      field: 'TaxCategory',
      headerName: 'Tax Category',
      type: 'number',
      ...commonColDef,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      cellClassName: 'actions',
      flex: 1,
      minWidth: 150,
      headerClassName: 'gridHeader--header',
      getActions: (params: GridRowParams) => {
        const { id, row } = params;
        const { PartitionKey, RowKey } = row;
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              key="save"
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              key="cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            key="edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <Tooltip title="View Mappings" key="mappings">
            <GridActionsCellItem
              icon={<ListAltIcon />}
              label="Mappings"
              onClick={() =>
                onManageMappings?.(
                  RowKey,
                  row.StockDebitLedger,
                  row.StockOCR,
                  PartitionKey,
                  row.MatchingStockAliases || [],
                )
              }
              color="inherit"
            />
          </Tooltip>,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            key="delete"
            label="Delete"
            onClick={handleDelete(PartitionKey, RowKey, row.StockOCR)}
            color="inherit"
          />,
        ];
      },
    },
  ];
};
